import { Box, Typography } from '@mui/material';
import { Cloud } from 'lucide-react';
import { useCallback, type ReactElement } from 'react';

import type { AdminApi, AdminUserActivity, AdminUserSession } from '@/admin/adminApi';
import { accountConflictsPath } from '@/admin/conflicts/conflictRoutes';
import { dayLabel } from '@/admin/pilotage/dashboardModel';
import { ReadBlock } from '@/admin/pilotage/ReadBlock';
import { useAdminRead, type AdminRead } from '@/admin/pilotage/useAdminRead';
import { StackedBarChart } from '@/ui/charts';
import { Badge, Button, Card, DataTable, EmptyState, type BadgeTone } from '@/ui/kit';

import { formatInstant } from './accountPresentation';
import { Fact, Facts } from './accountsUi';
import {
  ACTIVITY_DAYS,
  activityIsEmpty,
  describeSessionTruncation,
  describeTimezone,
  deviceLabel,
  entityTypeLabel,
  sessionState,
  type SessionStateKind,
} from './accountUsagePresentation';

/**
 * Ce que le serveur sait de l'usage d'un compte : sa synchronisation, son
 * activite, ses sessions.
 *
 * **Le serveur ne voit que ce qui lui est parvenu** : rien de ce qui attend dans
 * l'outbox d'un appareil. C'est dit sous chaque bloc qui en parle, parce qu'un
 * « dernier envoi : il y a 3 jours » se lit sinon « cette personne n'a rien
 * saisi depuis 3 jours ». Aucune de ces donnees n'est une charge utile : ce sont
 * des comptages (ADR 0035, donnees de sante).
 */

/** Lecture de l'activite, partagee par la carte « Synchronisation » et l'onglet « Activite ». */
export const useAccountActivity = (
  userId: string,
  refreshToken: number,
  enabled: boolean,
): { readonly state: AdminRead<AdminUserActivity>; readonly reload: () => void } => {
  const read = useCallback(
    (api: AdminApi) => api.getUserActivity(userId, ACTIVITY_DAYS),
    [userId],
  );

  return useAdminRead(read, refreshToken, enabled);
};

/**
 * Carte « Synchronisation » : dernier envoi, operations, conflits, sessions.
 *
 * Deux sources, **deux groupes** que rien ne doit melanger :
 * - le **journal d'idempotence** : operations acceptees et operations
 *   **refusees** sur la periode — ce que le serveur a recu, y compris l'historique
 *   anterieur au registre ;
 * - le **registre des conflits** (ADR 0039, D6), **chaque compteur a sa date** :
 *   detectes et resolus sur la periode (date de detection, date de resolution),
 *   ouverts a l'instant de la lecture, quelle que soit la date de detection. Ils
 *   ne se partagent pas : un conflit detecte avant la periode et resolu dedans est
 *   resolu sans etre detecte. « Ouverts » comprend les renvois a l'utilisatrice ;
 *   « resolus » comprend les conflits classes.
 *
 * Tant que la reprise de l'historique (#634) n'est pas jouee — et l'ecran ne peut
 * pas savoir simplement qu'elle l'a ete —, la carte le dit sous une forme neutre :
 * le registre peut compter moins de conflits que le journal ne compte d'operations
 * refusees. Un lien mene a la liste des conflits de ce compte, **sans fenetre**.
 */
export const SyncCard = ({
  userId,
  activity,
  onRetry,
  activeSessionCount,
  anonymized,
}: {
  userId: string;
  activity: AdminRead<AdminUserActivity>;
  onRetry: () => void;
  activeSessionCount: number;
  anonymized: boolean;
}): ReactElement => (
  <Card title="Synchronisation" icon={Cloud} subtitle="Ce que le serveur a reçu des appareils de ce compte">
    {anonymized ? (
      <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>
        Compte anonymisé : le serveur n’expose plus rien de son journal de synchronisation.
      </Typography>
    ) : (
      <ReadBlock state={activity} label="Synchronisation" onRetry={onRetry} skeletonHeight={120}>
        {({ sync }) => (
          <>
            <Facts>
              <Fact label="Dernier envoi">{formatInstant(sync.lastPushAt)}</Fact>
              <Fact label={`Opérations acceptées · ${String(ACTIVITY_DAYS)} j`}>{sync.acceptedInPeriod}</Fact>
              <Fact label={`Opérations refusées · ${String(ACTIVITY_DAYS)} j`}>{sync.conflictsInPeriod}</Fact>
              <Fact label="Sessions actives">{activeSessionCount}</Fact>
            </Facts>
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700 }}>
              Registre des conflits
            </Typography>
            <Facts>
              <Fact label={`Conflits détectés · ${String(ACTIVITY_DAYS)} j`}>{sync.conflicts.detected}</Fact>
              <Fact label="Conflits ouverts maintenant">{sync.conflicts.open}</Fact>
              <Fact label={`Conflits résolus · ${String(ACTIVITY_DAYS)} j`}>{sync.conflicts.resolved}</Fact>
              <Fact label={`Conflits sans objet · ${String(ACTIVITY_DAYS)} j`}>{sync.conflicts.notApplicable}</Fact>
            </Facts>
            <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              Détectés se compte à la date de détection, résolus et sans objet à la date de résolution, ouverts à
              l’instant de la lecture, quelle que soit la date de détection : ces nombres ne se partagent pas.
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              Les conflits antérieurs à la mise en service du registre n’apparaissent qu’après la reprise de
              l’historique (n° 634). Les opérations refusées viennent du journal et peuvent donc dépasser les
              conflits du registre. Seul ce que le serveur a reçu est compté : les saisies encore en attente dans un
              appareil ne sont pas visibles ici.
            </Typography>
            <Box>
              <Button size="S" to={accountConflictsPath(userId)}>
                Voir les conflits de ce compte
              </Button>
            </Box>
          </>
        )}
      </ReadBlock>
    )}
  </Card>
);

/** Onglet « Activite » : operations recues par jour et par nature, avec alternative textuelle. */
export const ActivityPanel = ({
  activity,
  onRetry,
  anonymized,
}: {
  activity: AdminRead<AdminUserActivity>;
  onRetry: () => void;
  anonymized: boolean;
}): ReactElement => {
  if (anonymized) {
    return (
      <EmptyState
        title="Activité non exposée"
        description="Compte anonymisé : le serveur n’expose plus ni l’activité ni les données de synchronisation de ce compte."
      />
    );
  }

  return (
    <ReadBlock state={activity} label="Activité" onRetry={onRetry} skeletonHeight={240}>
      {(data) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <StackedBarChart
            label={`Opérations reçues par jour, sur les ${String(data.days.length)} derniers jours`}
            categories={data.days.map((day) => dayLabel(day.date))}
            categoryHeader="Jour (UTC)"
            series={[{ label: 'Opérations reçues', values: data.days.map((day) => day.operations) }]}
            height={180}
          />
          <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
            {describeTimezone(data.timezone)} Heure de réception par le serveur, pas celle de l’appareil.
          </Typography>

          {activityIsEmpty(data) ? (
            <Typography sx={{ fontSize: 14 }}>
              Aucune opération reçue sur les {data.days.length} derniers jours.
            </Typography>
          ) : (
            <DataTable
              label="Opérations reçues par nature d’enregistrement"
              rows={data.byEntityType}
              getRowId={(row) => row.entityType}
              columns={[
                { key: 'type', header: 'Nature', render: (row) => entityTypeLabel(row.entityType), primary: true },
                { key: 'count', header: 'Opérations', render: (row) => row.count, align: 'right' },
              ]}
            />
          )}
        </Box>
      )}
    </ReadBlock>
  );
};

const SESSION_TONES: Readonly<Record<SessionStateKind, BadgeTone>> = {
  active: 'success',
  revoked: 'neutral',
  expired: 'warning',
};

/** Onglet « Appareils et sessions » : les 50 sessions les plus recentes, jamais un jeton. */
export const SessionsPanel = ({
  userId,
  refreshToken,
  anonymized,
}: {
  userId: string;
  refreshToken: number;
  anonymized: boolean;
}): ReactElement => {
  const read = useCallback((api: AdminApi) => api.listUserSessions(userId), [userId]);
  // Un compte anonymise n'expose plus ses sessions : aucune lecture inutile.
  const { state, reload } = useAdminRead(read, refreshToken, !anonymized);

  if (anonymized) {
    return (
      <EmptyState
        title="Aucune session"
        description="Compte anonymisé : ses appareils ont été déconnectés et le serveur n’en expose plus aucun."
      />
    );
  }

  return (
    <ReadBlock state={state} label="Sessions" onRetry={reload} skeletonHeight={160}>
      {(page) => {
        const truncation = describeSessionTruncation(page.items.length, page.total);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DataTable<AdminUserSession>
              label="Sessions du compte"
              rows={page.items}
              getRowId={(row) => row.id}
              emptyMessage="Ce compte n’a ouvert aucune session."
              columns={[
                { key: 'device', header: 'Appareil', render: (row) => deviceLabel(row), primary: true },
                {
                  key: 'state',
                  header: 'État',
                  render: (row) => {
                    const { kind, label } = sessionState(row);

                    return <Badge label={label} tone={SESSION_TONES[kind]} />;
                  },
                },
                { key: 'opened', header: 'Ouverte le', render: (row) => formatInstant(row.createdAt) },
                { key: 'activity', header: 'Dernière activité', render: (row) => formatInstant(row.lastActivityAt) },
                {
                  key: 'end',
                  header: 'Révoquée ou expire le',
                  render: (row) => formatInstant(row.revokedAt ?? row.refreshTokenExpiresAt),
                },
              ]}
            />
            {truncation === null ? null : (
              <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>{truncation}</Typography>
            )}
            <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              La dernière activité est le dernier renouvellement de la session, pas une connexion.
            </Typography>
          </Box>
        );
      }}
    </ReadBlock>
  );
};
